import { AlertState } from '@context/app-context';

import {
  attachDialogCallback,
  setIsMaintenance,
  setSystemDialogs,
} from '@store/slices/api.slice';
import {
  clearPOSMasterlist,
  clearPOSMenuImagesThunk,
} from '@modules/point-of-sale/slice/pos-masterlist.slice';
import { selectPOSstationID } from '@modules/point-of-sale/slice/pos-selector';
import { clearPOSStation } from '@modules/point-of-sale/slice/pos-stations.slice';
import { clearPOSTableArea } from '@modules/point-of-sale/slice/pos-table-area.slice';
import { removeSelectedAccountPeriod } from '@store/slices/accounting-and-finance/accg-selected-accounting-period.slice';

import { removeFOBookingAccountsList } from '@store/slices/front-office/fo-booking-accounts-list.slice';
import { removeBookingOptionList } from '@store/slices/front-office/fo-booking-option-list.slice';
import { useNavigate } from 'react-router-dom';
import { auth, TErrorRes, TLoginRes } from '../api/auth.api';
import { ChangePasswordParams, user } from '../api/user.api';
import { useAppDispatch, useAppSelector } from '../store';
import { resetGuide, setGuideUser } from '../store/slices/guide.slice';
import {
  clearPropertiesState,
  PropertyState,
  setActiveProperties,
  setSelectedProperty,
} from '../store/slices/property.slice';
import {
  removeLoginInfo,
  removeUser,
  saveUserThunk,
  setLoginInfo,
  IUserLogin,
  setMFALogin,
  setMFALoginLoading,
  clearMFALogin,
  setMFAStatus,
} from '../store/slices/user.slice';
import { CustomError, fetchHandleError } from '../utils/api-error-handling';
import { clearAllBrowserData, decrypt } from '../utils/security';
import useAppContext from './useAppContext';
import { UseDisclosureInterface } from './useDisclosure';
import useToast from './useToast';
import { moduleArr } from '@utils/module-arr';
import { SHA512 } from 'crypto-js';

/**
 * Interface for login form values
 */
interface FormValues {
  username: string;
  password: string;
}

interface IMFALoginResponse {
  status?: number;
  data?: (Partial<IUserLogin> & { sessionData?: IUserLogin }) | undefined;
}
interface ISessionIDSource {
  data?: {
    sessionData?: {
      user?: Record<string, unknown> & { sessionID?: string };
      sessionID?: string;
    };
    user?: Record<string, unknown> & { sessionID?: string };
    sessionID?: string;
  };
  user?: Record<string, unknown> & { sessionID?: string };
  sessionID?: string;
}

interface useAuthInterface {
  saveUser: (params: any, hideDialog?: boolean) => Promise<any>;
  logoutUser: (params: { sessionID: string; token: string }) => Promise<void>;
  loginUser: (data: FormValues) => Promise<
    | {
        data: IUserLogin;
      }
    | {
        deleteSession: boolean;
        data:
          | IUserLogin
          | {
              error_message: string;
              level: number;
              errors: {
                error: {
                  remarks: string;
                };
              }[];
              deleteSession: boolean;
              device: {
                os: string;
                browser: string;
              };
              usersID: string;
            };
        message:
          | string
          | {
              errMsg: string;
              errLevel: number;
            };
      }
    | undefined
  >;
  resendOTP: (
    otpLoadingState: any,
    resetTime: any,
    data: { username: string; password: string },
    token?: string,
  ) => Promise<any>;
  forgotPassword: (email: string) => Promise<boolean | undefined>;
  verifyToken: (token: string) => Promise<boolean | undefined>;
  changePassword: (params: {
    verifyPassword?: string;
    password?: string;
    token: string;
    username?: string;
    securityCode?: string;
  }) => Promise<any>;
  changePasswordOnly: (
    data: {
      userID?: string;
      password?: string;
      verifyPassword?: string;
      securityCode?: string;
      token?: string;
      signature: string;
    },
    noToast?: boolean,
  ) => Promise<
    | string
    | true
    | {
        errMsg: string;
        errLevel: number;
      }
    | undefined
  >;
  verifyDevice: (params: {
    deviceID: string;
    token: string;
  }) => Promise<boolean | undefined>;
  validateSession: (sessionID: string) => Promise<any>;
  generateOtpAlreadyLoggedIn: (userID: string) => Promise<any>;
  verifyGenerateOtpAlreadyLoggedIn: (params: {
    verificationCode: string;
    sessionID: string;
  }) => Promise<any>;
  verifyMFAAlreadyLoggedIn: (params: {
    verificationCode: string;
    sessionID: string;
    mfaLoginToken: string;
  }) => Promise<any>;
  verifySecCode: (secCode: string, priv: string) => Promise<any>;
  sendEmail: (route: 'security_code' | 'password') => Promise<any>;
  changePasswordV2: ({ route, data }: ChangePasswordParams) => Promise<any>;
  cleanUpState: () => void;
  loginMFAAuth: (params: {
    code: string;
    mfaLoginToken: string;
    signature: string;
    saveOnSuccess?: boolean;
    loginToken: string;
  }) => Promise<any>;
  setupMFAAuth: () => Promise<any>;
  verifyMFAAuth: (verificationCode: string) => Promise<any>;
  disableMFAAuth: () => Promise<any>;
  handleMFALogin: (code: string) => Promise<void>;
  updateSessionMfa: (userID: string) => Promise<any>;
  updateSessionLoggedInMfa: (userID: string) => Promise<any>;
  generateForceOTPMfa: (userID: string) => Promise<any>;
  submitMFAAlreadyLoggedIn: (
    params: {
      verificationCode: string;
      sessionID: string;
      mfa?: {
        token?: string;
      };
    },
    mfaVerificationCode?: string,
  ) => Promise<any>;
  getSessionData: (sessionID: string) => Promise<IUserLogin | undefined>;
}

const getMFALoginData = (
  res: IMFALoginResponse | undefined,
): IUserLogin | undefined =>
  (res?.data?.sessionData ?? res?.data) as IUserLogin | undefined;

const getMFASessionID = (
  ...sources: (ISessionIDSource | undefined)[]
): string | undefined => {
  for (const source of sources) {
    const sessionID =
      source?.data?.sessionData?.user?.sessionID ??
      source?.data?.sessionData?.sessionID ??
      source?.data?.user?.sessionID ??
      source?.data?.sessionID ??
      source?.user?.sessionID ??
      source?.sessionID;

    if (sessionID) return sessionID;
  }
  return undefined;
};

/**
 * Custom hook for authentication-related operations
 * @returns {Object} An object containing authentication functions
 */
const useAuth = (
  propToast?: (data: AlertState) => Promise<void>,
): useAuthInterface => {
  const navigate = useNavigate();
  const { posForage, setIsLoggingOut } = useAppContext();

  const toast = !propToast ? useToast()?.toast : propToast;
  const guideUser = useAppSelector((state) => state.guide.user);
  const loginInfo = useAppSelector((state) => state.user.loginInfo);
  const stationID = useAppSelector(selectPOSstationID);
  const mfaLogin = useAppSelector((state) => state.user.mfaLogin);
  const queuedSystemDialogs = useAppSelector(
    (state) => state.apiSlice.systemDialogs,
  );
  const dispatch = useAppDispatch();
  const { setCurrentDialogIndex, setStoreUser } = useAppContext();

  const cleanUpState = () => {
    dispatch(clearPropertiesState());
    dispatch(removeUser());
    dispatch(removeLoginInfo());
    dispatch(clearMFALogin());

    dispatch(removeFOBookingAccountsList());
    dispatch(removeBookingOptionList());
    dispatch(removeSelectedAccountPeriod());

    dispatch(setSystemDialogs([]));
    setStoreUser(undefined);
    // POS
    dispatch(clearPOSStation(true));
    dispatch(clearPOSMasterlist(true));
    dispatch(clearPOSTableArea(true));
    if (stationID) {
      dispatch(clearPOSMenuImagesThunk({ stationID, posForage }));
    }

    clearAllBrowserData();
  };

  /**
   * Handles and displays errors
   * @param {any} error - The error to handle
   */
  const handleErr = (error: any) => {
    if (error instanceof CustomError) {
      toast({
        message: error.data?.errMsg || error.data,
        // Dynamic severity from old and new res
        severity:
          error.data?.severity ||
          (error.data?.errLevel === 1 ? 'warning' : 'error'),
        duration: 6000,
      });
    } else {
      toast({
        message: error?.toString(),
        severity: 'error',
        duration: 6000,
      });
    }
    throw error;
  };

  /**
   * Logs in a user
   * @param {FormValues} data - The login form data
   * @returns {Promise<Promise<void>>}
   */
  const loginUser = async (data: FormValues) => {
    try {
      const res = await auth.login(data);

      if (!res) {
        throw new Error('Login failed: Server Error');
      }

      const responseData = res.data as (TLoginRes['data'] &
        TErrorRes['data']) & { signature?: string };
      const dialogs = responseData?.systemDialogs ?? [];

      const mfaLoginToken = responseData?.mfa?.token;
      const loginToken = responseData?.token;

      /**
       * Maintenance Mode
       */
      if (responseData?.isMaintenanceMode && dialogs.length > 0) {
        // toast({
        //   message:
        //     "We're currently making some updates to improve your experience.\nPlease check back soon, and thank you for your patience!",
        //   severity: 'error',
        // });
        dispatch(setIsMaintenance(true));
        dispatch(setSystemDialogs(dialogs));
        setCurrentDialogIndex(0);
        return;
      }

      /**
       * Session deleted from server
       */
      if (!!responseData?.deleteSession) {
        return {
          deleteSession: true,
          data: responseData,
          message: fetchHandleError(res),
        };
      }

      /**
       * Check if MFA is required
       */
      if (responseData?.mfa?.require) {
        if (!mfaLoginToken) {
          throw new CustomError({
            errMsg:
              'MFA token not found in response from server. MFA setup may be incomplete.',
            errLevel: 2,
          });
        }
        if (!loginToken) {
          throw new CustomError({
            errMsg:
              'Login session token not found. Please try logging in again.',
            errLevel: 2,
          });
        }

        dispatch(
          setMFALogin({
            mfaLoginToken,
            loginToken,
            data: responseData as TLoginRes['data'],
          }),
        );
        dispatch(setMFAStatus(true));
        return;
      }

      /**
       * Verified device → skip OTP
       */
      if (
        res.status === 200 &&
        responseData?.device?.verified &&
        !responseData?.mfa?.require
      ) {
        await saveUser({ ...responseData, ...data });
        return;
      }

      /**
       * Continue to OTP flow
       */
      if (res.status === 200 && !responseData?.mfa?.require) {
        const otp = decrypt(responseData?.device?.verificationCode);

        const goToOtp = async () => {
          dispatch(setLoginInfo(responseData as IUserLogin));
          navigate('/otp-verification', {
            state: {
              ...data,
              otp,
            },
          });
        };

        if (dialogs.length > 0) {
          const allNotification = dialogs.every(
            (d) => d.dialog_type === 'Notification Message',
          );

          if (allNotification) {
            dispatch(setSystemDialogs(dialogs));
            goToOtp();
            return;
          }

          dispatch(
            setSystemDialogs(
              attachDialogCallback({
                dialogs,
                cb: goToOtp,
              }),
            ),
          );
          return;
        }

        goToOtp();
        return;
      }

      throw new CustomError(fetchHandleError(res));
    } catch (error) {
      handleErr(error);
    }
  };

  /**
   * Handles the MFA login verification step.
   *
   * Dispatches loading state, calls `loginMFAAuth` with the user-entered
   * 6-digit code along with the mfaLoginToken and signature stored in Redux,
   * then clears MFA state on success.
   *
   * @param {string} code - The 6-digit MFA code entered by the user
   * @returns {Promise<void>}
   *
   * @example
   * Called from DialogLoginMFA when user submits their code
   * <DialogLoginMFA onSubmit={handleMFALogin} />
   */
  const handleMFALogin = async (code: string) => {
    const signature = SHA512(`${mfaLogin.mfaLoginToken + code}`).toString();
    dispatch(setMFALoginLoading(true));

    try {
      const result = await loginMFAAuth({
        code,
        mfaLoginToken: mfaLogin.mfaLoginToken,
        signature,
        saveOnSuccess: true,
        loginToken: mfaLogin.loginToken,
      });

      if (result?.error || result?.status === 'error') {
        handleErr(result);
        return;
      }

      await completeMFALogin(result, mfaLogin?.data);
      dispatch(clearMFALogin());
    } catch (error) {
      handleErr(error);
    } finally {
      dispatch(setMFALoginLoading(false));
    }
  };

  /**
   * Saves user data and sets up the session
   * @param {IUserLogin} data - The user data to save
   * @returns {Promise<Promise<void>>}
   */
  const saveUser = async (data: IUserLogin, hideDialog?: boolean) => {
    try {
      //  if (data?.isMaintenanceMode && data?.systemDialogs?.length <= 0) {
      //   toast({
      //     severity: 'warning',
      //     message: 'Maintenance in progress. Please try again later.',
      //   });
      //   return;
      // }

      // if (data?.isMaintenanceMode && data?.systemDialogs?.length > 0) {
      //   toast({
      //     severity: 'warning',
      //     message: 'Maintenance in progress. Please try again later.',
      //   });
      //   dispatch(setSystemDialogs(data.systemDialogs));
      //   setCurrentDialogIndex(0);
      //   return;
      // }
      if (data?.isMaintenanceMode) {
        const maintenanceDialog = data?.systemDialogs?.find(
          (dialog) => dialog?.message,
        ) ??
          queuedSystemDialogs.find((dialog) => dialog?.message) ?? {
            message:
              'We are currently performing system maintenance to improve performance and provide a better experience. Please try again shortly. Thank you for your patience and understanding.',
            dialog_type: 'Dialog Box',
          };

        dispatch(setSystemDialogs([maintenanceDialog]));
        setCurrentDialogIndex(0);
        return;
      }
      if (data?.systemDialogs?.length > 0 && !hideDialog) {
        dispatch(setSystemDialogs(data.systemDialogs));
        setCurrentDialogIndex(0);
        setStoreUser(data);
        return { data };
      }
      if (data?.mfa?.require !== undefined) {
        dispatch(setMFAStatus(data.mfa.require));
      }

      if (!guideUser || guideUser !== data?.user?.id || data?.userID) {
        dispatch(resetGuide({}));
        dispatch(setGuideUser(data?.user?.id || data?.userID));
      }

      const userProperties = await auth.validateModule(data.token);

      const properties =
        userProperties.data.properties?.map(
          ({ property }: { property: PropertyState }) => property,
        ) ?? [];

      const mappedProperties = properties
        .toSorted((a, b) =>
          (a.propertyName || '').localeCompare(
            b.propertyName || '',
            undefined,
            { sensitivity: 'base' },
          ),
        )
        .map((property) => ({
          label: property.propertyName || 'N/A',
          value: property.propertyID || 'N/A',
          activeModule:
            property.userData?.Modules?.flatMap(({ Module }) => {
              const currentModule = moduleArr.find(
                (item) => item.code === Module.Code,
              );

              return currentModule?.isActive
                ? [currentModule.link.split('/')[1]]
                : [];
            }) ?? [],
        }));

      const selectedProperty = mappedProperties[0];

      if (selectedProperty) dispatch(setSelectedProperty(selectedProperty));

      const selectedModules =
        properties.find(
          (property) => property.propertyID === selectedProperty.value,
        )?.userData?.Modules ?? [];

      const activeProperties = moduleArr.map((module) => ({
        link: module.link,
        isActive:
          module.isActive &&
          selectedModules.some(
            (item) => item.Module.Code === module.code && item.Module.isActive,
          ),
        disableOnAllProperty: false,
      }));

      dispatch(setActiveProperties(activeProperties));

      await dispatch(saveUserThunk(data)).unwrap();

      navigate('/');
    } catch (error) {
      handleErr(error);
    }
  };

  /**
   * Logs out a user
   * @param {{ sessionID: string; token: string }} data - The logout data
   * @returns {Promise<Promise<void>>}
   */
  const logoutUser = async (data: { sessionID: string; token: string }) => {
    try {
      setIsLoggingOut(true);
      const res = await auth.logout(data);
      if (res?.status == 200) {
        cleanUpState();
        return navigate('/login');
      }
      throw new CustomError(fetchHandleError(res));
    } catch (error) {
      handleErr(error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  /**
   * Resends OTP
   * @param {any} otpLoadingState - The OTP loading state
   * @param {any} resetTime - Function to reset the timer
   * @param {{ username: string; password: string }} _data - The user data
   * @param {string} token - The OTP token for resending (priority)
   * @returns {Promise<any>}
   */
  const resendOTP = async (
    otpLoadingState: UseDisclosureInterface,
    resetTime: () => void,
    _data: { username: string; password: string },
  ) => {
    try {
      otpLoadingState.onOpen();
      if (loginInfo.token) {
        const res = await auth.otp(loginInfo.token);
        if (res?.status == 200) {
          otpLoadingState.onClose();
          resetTime();
          toast({ message: 'Success Resend OTP', severity: 'success' });
          return res;
        }

        otpLoadingState.onClose();
        throw new CustomError(fetchHandleError(res));
      }
      otpLoadingState.onClose();
      throw new Error(`Resend OTP failed: No Token Decrypt`);
    } catch (error) {
      handleErr(error);
    }
  };

  /**
   * Initiates the forgot password process
   * @param {string} email - The user's email
   * @returns {Promise<boolean>}
   */
  const forgotPassword = async (email: string) => {
    try {
      const res = await user.forgetPasswordToken(email);
      if (res?.status == 200) {
        return true;
      }
      throw new CustomError(fetchHandleError(res));
    } catch (error) {
      handleErr(error);
    }
  };

  /**
   * Verifies the forgot password token
   * @param {string} token - The token to verify
   * @returns {Promise<boolean>}
   */
  const verifyToken = async (token: string) => {
    try {
      const res = await user.verifyFPToken(token);
      if (res?.status == 200) {
        return true;
      }
      throw new CustomError(fetchHandleError(res));
    } catch (error) {
      handleErr(error);
    }
  };

  /**
   * Changes the user's password
   * @param {{ verifyPassword?: string; password?: string; token: string; username?: string; securityCode?: string }} data - The password change data
   * @returns {Promise<Promise<void>>}
   */
  const changePassword = async (data: {
    verifyPassword?: string;
    password?: string;
    token: string;
    username?: string;
    securityCode?: string;
  }) => {
    try {
      const res = await user.changePassword(data);
      if (res?.status == 200) {
        toast({ message: res?.data?.message, severity: 'success' });
        return navigate('/');
      }
      throw new CustomError(fetchHandleError(res));
    } catch (error) {
      handleErr(error);
    }
  };

  /**
   * Changes only the user's password
   * @param {{ userID?: string; password?: string; verifyPassword?: string; securityCode?: string; token?: string; signature: string }} data - The password change data
   * @param {boolean} noToast - Whether to suppress toast notifications
   * @returns {Promise<boolean | string>}
   */
  const changePasswordOnly = async (
    data: {
      userID?: string;
      password?: string;
      verifyPassword?: string;
      securityCode?: string;
      token?: string;
      signature: string;
    },
    noToast?: boolean,
  ) => {
    try {
      const res = await user.changePasswordOnly(data);
      if (res?.status == 200) {
        return true;
      }
      if (noToast) {
        return fetchHandleError(res);
      } else {
        throw new CustomError(fetchHandleError(res));
      }
    } catch (error) {
      handleErr(error);
    }
  };

  /**
   * Verifies a device
   * @param {{ deviceID: string; token: string }} data - The device verification data
   * @returns {Promise<boolean>}
   */
  const verifyDevice = async (data: { deviceID: string; token: string }) => {
    try {
      const res = await auth.verifyDevice(data);

      if (res?.status == 201) {
        return true;
      }

      throw new CustomError(fetchHandleError(res));
    } catch (error) {
      handleErr(error);
    }
  };

  /**
   * Validates a session
   * @param {string} sessionID - The session ID to validate
   * @returns {Promise<Promise<void>>}
   */
  const validateSession = async (sessionID: string) => {
    try {
      const res = await auth.validateSession(sessionID);

      if (!res?.status) {
        throw new Error('Login failed: Server Error');
        return;
      }

      if (res?.status == 200) {
        return await saveUser(res.data);
      }

      throw new CustomError(fetchHandleError(res));
      navigate('/');
    } catch (error) {
      handleErr(error);
    }
  };

  const getSessionData = async (sessionID: string) => {
    try {
      const res = await auth.validateSession(sessionID);

      if (!res?.status) {
        throw new Error('Login failed: Server Error');
      }

      if (res?.status == 200) {
        return res.data as IUserLogin;
      }

      throw new CustomError(fetchHandleError(res));
    } catch (error) {
      handleErr(error);
    }
  };

  /**
   * Generates OTP for an already logged-in user
   * @param {string} userID - The user ID
   * @returns {Promise<any>}
   */
  const generateOtpAlreadyLoggedIn = async (userID: string) => {
    try {
      const res = await auth.generateOtpAlreadyLoggedIn(userID);

      if (!res?.status) {
        throw new Error('Login failed: Server Error');
        return;
      }

      if (res?.status == 201) {
        return res;
      }

      throw new CustomError(fetchHandleError(res));
    } catch (error) {
      handleErr(error);
    }
  };

  /**
   * Verifies OTP for an already logged-in user
   * @param {{ verificationCode: string; sessionID: string }} data - The verification data
   * @returns {Promise<any>}
   */
  const verifyGenerateOtpAlreadyLoggedIn = async (data: {
    verificationCode: string;
    sessionID: string;
  }) => {
    try {
      const res = await auth.verifyGenerateOtpAlreadyLoggedIn(data);

      if (!res?.status) {
        throw new Error('Login failed: Server Error');
        return;
      }

      if (res?.status == 200) {
        return res;
      }

      throw new CustomError(fetchHandleError(res));
    } catch (error) {
      handleErr(error);
    }
  };

  const verifyMFAAlreadyLoggedIn = async (data: {
    verificationCode: string;
    sessionID: string;
    mfaLoginToken: string;
  }) => {
    try {
      const res = await auth.verifyMFAAlreadyLoggedIn(data);

      if (!res?.status) {
        throw new Error('Login failed: Server Error');
      }

      if (res?.status == 200 || res?.status == 201) {
        return res;
      }

      throw new CustomError(fetchHandleError(res));
    } catch (error) {
      handleErr(error);
    }
  };

  /**
   * Verifies a security code
   * @param {string} secCode - The security code to verify
   * @param {string} priv - The privilege level
   * @returns {Promise<any>}
   */
  const verifySecCode = async (secCode: string, priv: string) => {
    try {
      const res = await auth.verifySecCode(secCode, priv);

      if (!res?.status) {
        throw new Error('Server Error');
        return;
      }

      return res;
    } catch (error) {
      handleErr(error);
    }
  };

  const sendEmail = async (route: 'security_code' | 'password') => {
    try {
      const res = await user.sendEmail(route);
      if (!res?.status) {
        throw new Error('Server Error');
      }

      if (res?.status >= 200 && res?.status <= 299) {
        toast({
          message: `Success, kindly check your email for temporary ${
            route === 'password' ? route : 'security code'
          }`,
          severity: 'success',
        });
        return res;
      }

      throw new CustomError(fetchHandleError(res));
    } catch (error) {
      handleErr(error);
    }
  };

  const changePasswordV2 = async ({ route, data }: ChangePasswordParams) => {
    try {
      const res = await user.changePasswordV2({
        route,
        data,
      } as ChangePasswordParams);

      if (!res?.status) {
        throw new Error('Server Error');
      }

      if (res?.status >= 200 && res?.status <= 299) {
        return res;
      }

      throw new CustomError(fetchHandleError(res));
    } catch (error) {
      handleErr(error);
    }
  };

  /**
   * Login Using MFA authentication
   * @param {{ code: string; token: string, signature: string }} data - The verification data
   * @returns {Promise<any>}
   */
  const loginMFAAuth = async (data: {
    mfaLoginToken: string;
    code: string;
    signature: string;
    saveOnSuccess?: boolean;
    loginToken: string;
  }) => {
    try {
      const res = await auth.loginMFAAuth({
        code: data.code,
        mfaLoginToken: data.mfaLoginToken,
        signature: data.signature,
        loginToken: data.loginToken,
      });

      if (!res?.status) {
        throw new Error('Login failed: Server Error');
        return;
      }

      if (res?.status == 200) {
        return res;
      }

      throw new CustomError(fetchHandleError(res));
    } catch (error) {
      handleErr(error);
    }
  };

  /**
   * Setup MFA authentication
   * @returns {Promise<any>}
   */
  const setupMFAAuth = async () => {
    try {
      const res = await auth.setupMFAAuth();

      if (!res?.status) {
        throw new Error('Setup failed: Server Error');
        return;
      }

      if (res?.status == 200) {
        return res;
      }
      throw new CustomError(fetchHandleError(res));
    } catch (error) {
      handleErr(error);
    }
  };

  /**
   * Verifies MFA authentication
   * @param {{ verificationCode: string}} data - The verification data
   * @returns {Promise<any>}
   */
  const verifyMFAAuth = async (verificationCode: string) => {
    try {
      const res = await auth.verifyMFAAuth({ verificationCode });

      if (!res?.status) {
        throw new Error('Verify failed: Server Error');
        return;
      }

      if (res?.status == 200) {
        dispatch(setMFAStatus(true));
        return res;
      }

      throw new CustomError(fetchHandleError(res));
    } catch (error) {
      handleErr(error);
    }
  };

  /**
   * Disable MFA authentication
   * @param {{ verificationCode: string}} data - The verification data
   * @returns {Promise<any>}
   */
  const disableMFAAuth = async () => {
    try {
      const res = await auth.disableMFAAuth();

      if (!res?.status) {
        throw new Error('Disable failed: Server Error');
        return;
      }

      if (res?.status == 200) {
        dispatch(setMFAStatus(false));
        return res;
      }

      throw new CustomError(fetchHandleError(res));
    } catch (error) {
      handleErr(error);
    }
  };

  /**
   * PUT /session/:userID/otp (updateSessionMfa)
   * Requests an OTP for the MFA login flow ("Use OTP instead").
   * Returns only verificationCode + sessionID — no token yet.
   */
  const updateSessionMfa = async (userID: string) => {
    try {
      const res = await auth.updateSessionMfa(userID);

      if (!res?.status) {
        throw new Error('Update Session failed');
      }

      if (res?.status == 201) {
        return res;
      }

      throw new CustomError(fetchHandleError(res));
    } catch (error) {
      handleErr(error);
    }
  };

  /**
   * PUT /session/:userID/mfa (updateSessionLoggedInMfa)
   * Re-triggers an MFA/OTP challenge for a user who is already logged in
   * (e.g. verifying a sensitive action mid-session), not the initial login OTP flow.
   */
  const updateSessionLoggedInMfa = async (userID: string) => {
    try {
      const res = await auth.updateSessionLoggedInMfa(userID);

      if (!res?.status) {
        throw new Error('Login failed: Server Error');
      }

      if (res?.status == 201) {
        return res;
      }

      throw new CustomError(fetchHandleError(res));
    } catch (error) {
      handleErr(error);
    }
  };

  /**
   * PUT /session/:userID/otp (generateForceOTPMfa)
   * Forces an OTP to be generated/sent regardless of the user's MFA enrollment
   * status; always resolves with isMFA: false since MFA isn't actually involved.
   */
  const generateForceOTPMfa = async (userID: string) => {
    try {
      const res = await auth.generateForceOTPMfa(userID);

      if (!res?.status) {
        throw new Error('Login failed: Server Error');
      }

      if (res?.status == 201) {
        return res;
      }

      throw new CustomError(fetchHandleError(res));
    } catch (error) {
      handleErr(error);
    }
  };

  const submitMFAAlreadyLoggedIn = async (
    payload: {
      verificationCode: string;
      sessionID: string;
      mfa?: {
        token?: string;
      };
    },
    mfaVerificationCode?: string,
  ) => {
    if (mfaVerificationCode) {
      const mfaLoginToken = payload.mfa?.token;

      if (!mfaLoginToken) {
        throw new Error('MFA token not found');
      }

      const renewSessionRes = await verifyMFAAlreadyLoggedIn({
        verificationCode: mfaVerificationCode,
        sessionID: payload.sessionID,
        mfaLoginToken,
      });
      // Verification errors are already reported by the renewal hook.
      if (!renewSessionRes) return;

      const renewedSessionID = getMFASessionID(renewSessionRes);

      if (!renewedSessionID) {
        throw new Error('Renewed session ID not found');
      }

      // The session endpoint verifies MFA and renews the session in one step.
      // Load and save the renewed login just like the OTP renewal flow.
      await validateSession(renewedSessionID);
      return;
    }

    const res = await verifyGenerateOtpAlreadyLoggedIn(payload);
    const sessionData = res?.data?.sessionData;
    const sessionID =
      sessionData?.user?.sessionID ??
      sessionData?.sessionID ??
      payload.sessionID;

    if (sessionData?.token) {
      await saveUser(sessionData);
      return;
    }

    if (sessionID) {
      await validateSession(sessionID);
    }
  };

  const completeMFALogin = async (
    mfaResult: IMFALoginResponse,
    fallbackData?: IUserLogin,
  ) => {
    const loginData = getMFALoginData(mfaResult);

    if (loginData?.token) {
      await saveUser(loginData);
      return;
    }

    const sessionID = getMFASessionID(mfaResult, fallbackData);

    if (!sessionID) {
      throw new Error('Verified MFA session data not found');
    }

    await validateSession(sessionID);
  };

  return {
    cleanUpState,
    saveUser,
    logoutUser,
    loginUser,
    resendOTP,
    forgotPassword,
    verifyToken,
    changePassword,
    changePasswordOnly,
    verifyDevice,
    validateSession,
    getSessionData,
    generateOtpAlreadyLoggedIn,
    verifyGenerateOtpAlreadyLoggedIn,
    verifyMFAAlreadyLoggedIn,
    verifySecCode,
    sendEmail,
    changePasswordV2,
    loginMFAAuth,
    setupMFAAuth,
    verifyMFAAuth,
    handleMFALogin,
    disableMFAAuth,
    updateSessionLoggedInMfa,
    updateSessionMfa,
    generateForceOTPMfa,
    submitMFAAlreadyLoggedIn,
  };
};

export default useAuth;
